import type { AnyExtension, Editor } from '@tiptap/core';
import CodeBlock from '@tiptap/extension-code-block';
import Placeholder from '@tiptap/extension-placeholder';
import { Table } from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import { NodeSelection, Selection, TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef } from 'react';
import { Markdown as TiptapMarkdown } from 'tiptap-markdown';
import { BlockIds } from './extensions/block-ids-extension';
import { ClickToAppend } from './extensions/click-to-append-extension';
import { MarkdownHardBreak, MarkdownText } from './extensions/markdown-fidelity-extension';
import type { SlashCommandItem } from './extensions/slash-command';
import { SlashCommand, slashCommandPluginKey } from './extensions/slash-command';
import { TableControls } from './extensions/table-controls-extension';
import { BlockDragHandle } from './internals/block-drag-handle';
import { BubbleToolbar } from './internals/bubble-toolbar';
export type NotionEditorProps = {
  content: string;
  onChange?: (markdown: string) => void;
  onBlur?: () => void;
  editable?: boolean;
  onEditorReady?: (editor: Editor) => void;
  // Tiptap v3 fires `onChange` once on mount with the initial `content`. Consumers that
  // treat every `onChange` as a user edit should pass `false` to skip that emission.
  emitOnMount?: boolean;
  // Appended to the built-in extension list. Read once at editor construction —
  // pass a stable array; later changes to it are ignored.
  extensions?: AnyExtension[];
  slashCommands?: SlashCommandItem[];
  // Shown while the document is empty, in place of the generic slash-command hint.
  placeholder?: string;
};
const getMarkdown = (editor: unknown): string => (editor as {
  storage: {
    markdown: {
      getMarkdown: () => string;
    };
  };
}).storage.markdown.getMarkdown();

// A document that opens on a block has no text position before it, so ProseMirror's default selection
// lands on the block, rendering it selected before anyone has touched the editor. A document made
// entirely of blocks has no text position at all, and is given a paragraph so it has one — the only
// case worth appending for, since an appended paragraph outlives the load and reaches the markdown.
const settleInitialSelection = (editor: Editor) => {
  if (!(editor.state.selection instanceof NodeSelection)) return;
  const {
    doc,
    tr,
    schema
  } = editor.state;
  const caret = Selection.findFrom(doc.resolve(0), 1, true);
  if (caret) {
    editor.view.dispatch(tr.setSelection(caret).setMeta('addToHistory', false));
    return;
  }
  tr.insert(doc.content.size, schema.nodes.paragraph.create());
  const appended = Selection.findFrom(tr.doc.resolve(tr.doc.content.size), -1, true);
  if (appended) tr.setSelection(appended);
  editor.view.dispatch(tr.setMeta('addToHistory', false));
};
const MAX_RECENT_EMITS = 20;
export const NotionEditor = ({
  content,
  onChange,
  onBlur,
  editable = true,
  onEditorReady,
  emitOnMount = true,
  extensions,
  slashCommands,
  placeholder
}: NotionEditorProps) => {
  const prevContent = useRef(content);
  const contentRef = useRef(content);
  const slashCommandsRef = useRef(slashCommands ?? []);
  const placeholderRef = useRef(placeholder);
  placeholderRef.current = placeholder;
  const initialSyncDoneRef = useRef(false);
  // emitUpdate:false can't fully suppress the mount emit — it re-fires asynchronously via a
  // plugin transaction. So when emitOnMount is false, remember the initial markdown and
  // swallow the emit that matches it, clearing the guard on the first real edit.
  const initialMarkdownRef = useRef<string | null>(null);
  // What this editor has emitted recently. A `content` prop matching one of these is the parent
  // echoing the editor's own typing back (possibly a keystroke behind); anything else is an edit
  // from outside — Percy, a refetch, another tab — and must land even while the editor has focus,
  // or the next keystroke would emit the stale document and save over it.
  const recentEmitsRef = useRef<string[]>([]);

  // onCreate captures contentRef at construction time, so keep it fresh.
  useEffect(() => {
    contentRef.current = content;
  }, [content]);
  useEffect(() => {
    slashCommandsRef.current = slashCommands ?? [];
  }, [slashCommands]);
  const syncContent = (e: Editor, next: string, {
    focused
  }: {
    focused: boolean;
  }) => {
    if (next === prevContent.current) return;
    if (focused && recentEmitsRef.current.includes(next)) return;
    // Replacing the doc while the slash menu is open rebuilds ProseMirror and
    // tears down the tippy portal — leave it alone until the menu closes.
    const suggestion = slashCommandPluginKey.getState(e.state) as {
      active?: boolean;
    } | undefined;
    if (suggestion?.active) return;
    prevContent.current = next;
    recentEmitsRef.current = [];
    const {
      from,
      to
    } = e.state.selection;
    e.chain().command(({
      tr
    }) => {
      tr.setMeta('addToHistory', false);
      return true;
    }).setContent(next, {
      emitUpdate: false
    }).command(({
      tr: tr_0,
      dispatch
    }) => {
      if (!dispatch) return true;
      const size = tr_0.doc.content.size;
      const nextFrom = Math.min(from, size);
      const nextTo = Math.min(to, size);
      // `between`, not `create`: clamping to the document's size can land on the document itself
      // rather than inside inline content — which `create` throws on, and a throw here happens
      // during a content sync and takes the editor down with it. A prompt whose last node is a
      // block is exactly that case, so this is reachable whenever a task ends in one.
      tr_0.setSelection(TextSelection.between(tr_0.doc.resolve(nextFrom), tr_0.doc.resolve(nextTo)));
      return true;
    }).run();
    settleInitialSelection(e);
  };
  const editor = useEditor({
    immediatelyRender: false,
    editable,
    extensions: [StarterKit.configure({
      heading: {
        levels: [1, 2, 3]
      },
      codeBlock: false,
      link: {
        openOnClick: false
      },
      dropcursor: {
        width: 2,
        class: 'notion-dropcursor'
      },
      // Off: it re-appends a paragraph on the next transaction of any kind — a click is enough — so
      // the empty paragraph under a block could never be deleted, and a document that ends on a block
      // grew one on load that its markdown never had. ClickToAppend is how you add one deliberately.
      trailingNode: false,
      text: false,
      hardBreak: false
    }), MarkdownText, MarkdownHardBreak, Placeholder.configure({
      placeholder: ({
        editor: e_0,
        node
      }) => {
        if (node.type.name === 'heading') {
          return `Heading ${node.attrs.level}`;
        }
        if (placeholderRef.current && e_0.isEmpty) return placeholderRef.current;
        return "Press '/' for commands";
      },
      showOnlyWhenEditable: true,
      showOnlyCurrent: true
    }), TaskList, TaskItem.configure({
      nested: true
    }), Table.configure({
      resizable: true,
      renderWrapper: true
    }), TableRow, TableHeader, TableCell, TableControls, BlockIds, ClickToAppend, CodeBlock, SlashCommand.configure({
      getExtraCommands: () => slashCommandsRef.current
    }), TiptapMarkdown.configure({
      html: true,
      transformPastedText: true,
      transformCopiedText: true,
      breaks: true
    }), ...(extensions ?? [])],
    content: '',
    onCreate: ({
      editor: e_1
    }) => {
      if (contentRef.current) {
        e_1.commands.setContent(contentRef.current, {
          emitUpdate: emitOnMount
        });
        prevContent.current = contentRef.current;
        if (!emitOnMount) initialMarkdownRef.current = getMarkdown(e_1);
      }
      settleInitialSelection(e_1);
      onEditorReady?.(e_1);
    },
    onUpdate: ({
      editor: e_2
    }) => {
      const markdown = getMarkdown(e_2);
      if (initialMarkdownRef.current !== null) {
        if (markdown === initialMarkdownRef.current) return;
        initialMarkdownRef.current = null;
      }
      prevContent.current = markdown;
      recentEmitsRef.current = [...recentEmitsRef.current.slice(-(MAX_RECENT_EMITS - 1)), markdown];
      onChange?.(markdown);
    },
    onBlur: ({
      editor: e_3
    }) => {
      syncContent(e_3, contentRef.current, {
        focused: false
      });
      onBlur?.();
    },
    editorProps: {
      attributes: {
        class: 'notion-editor focus:outline-none'
      }
    }
  });
  useEffect(() => {
    if (!editor) return;
    // After the editor is constructed, if onCreate ran with empty contentRef
    // (e.g. consumer wasn't yet rendered when useEditor lazily fired), force
    // a single content sync so the initial markdown actually lands.
    if (!initialSyncDoneRef.current) {
      initialSyncDoneRef.current = true;
      if (content && editor.isEmpty) {
        editor.commands.setContent(content, {
          emitUpdate: false
        });
        prevContent.current = content;
        if (!emitOnMount) initialMarkdownRef.current = getMarkdown(editor);
        settleInitialSelection(editor);
        return;
      }
    }
    syncContent(editor, content, {
      focused: editor.isFocused
    });
  }, [content, editor]);

  // `editable` is only read at construction, so sync it on prop changes.
  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);
  if (!editor) return null;
  return (
    // `isolate` traps the table controls' z-indexes (add-row/col buttons, cell overlay) in this
    // wrapper's own stacking context, so they can't paint over app-chrome overlays (popovers,
    // drawers) that portal to the body.
    <div className="notion-editor-wrapper relative isolate">
      {editable && <BubbleToolbar editor={editor} />}
      {/* The drag-handle plugin moves the handle's element into its own wrapper, so unmounting the
          handle alone makes React remove a node from a parent it no longer sits in. Wrapped, React
          removes this box instead, and the plugin tears down the element it moved. */}
      {editable && <div className="contents">
          <BlockDragHandle editor={editor} />
        </div>}
      <EditorContent editor={editor} />
    </div>
  );
};
export default NotionEditor;